import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CallToAction = () => {
  const membershipTiers = [
    {
      name: "Starter",
      price: "Free",
      period: "forever",
      description: "Perfect for new collectors getting started",
      features: [
        "Track up to 50 items",
        "Basic market insights",
        "Community access",
        "Mobile app access"
      ],
      cta: "Start Free",
      popular: false,
      href: "/registration"
    },
    {
      name: "Professional",
      price: "$29",
      period: "per month",
      description: "For serious collectors building valuable portfolios",
      features: [
        "Unlimited item tracking",
        "Advanced analytics & reports",
        "Real-time market alerts",
        "Expert authentication network",
        "Priority customer support",
        "Tax reporting tools"
      ],
      cta: "Start 14-Day Trial",
      popular: true,
      href: "/registration"
    },
    {
      name: "Enterprise",
      price: "$99",
      period: "per month",
      description: "For dealers, galleries, and institutional collectors",
      features: [
        "Everything in Professional",
        "Multi-user team access",
        "API integration",
        "Custom reporting",
        "Dedicated account manager",
        "White-label options"
      ],
      cta: "Contact Sales",
      popular: false,
      href: "/registration"
    }
  ];

  const quickStartSteps = [
    {
      step: 1,
      title: "Create Account",
      description: "Sign up in 30 seconds with your email",
      icon: "UserPlus"
    },
    {
      step: 2,
      title: "Add Your First Item",
      description: "Upload photos and basic details",
      icon: "Plus"
    },
    {
      step: 3,
      title: "Get Instant Insights",
      description: "See market value and trends immediately",
      icon: "TrendingUp"
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-background to-muted/20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Main CTA Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-6xl font-bold text-foreground mb-6">
            Ready to Transform Your Collection?
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Join thousands of collectors who've already discovered the power of professional portfolio management. Start your journey today.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Button
              variant="default"
              size="xl"
              iconName="Gem"
              iconPosition="left"
              className="px-12 py-4 text-xl font-bold liquid-gold premium-hover"
              onClick={() => window.location.href = '/registration'}
            >
              Start Free Today
            </Button>
            
            <Button
              variant="outline"
              size="xl"
              iconName="Play"
              iconPosition="left"
              className="px-12 py-4 text-xl font-medium premium-hover"
              onClick={() => window.location.href = '/collection-dashboard'}
            >
              View Demo
            </Button>
          </div>
        </motion.div>

        {/* Quick Start Steps */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-2xl font-bold text-foreground text-center mb-12">
            Get Started in 3 Simple Steps
          </h3>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {quickStartSteps?.map((step, index) => (
              <div key={step?.step} className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon name={step?.icon} size={24} className="text-primary" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-bold">
                    {step?.step}
                  </div>
                </div>
                <h4 className="text-xl font-semibold text-foreground mb-3">
                  {step?.title}
                </h4>
                <p className="text-muted-foreground">
                  {step?.description}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Membership Tiers */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-foreground mb-4">
              Choose Your Plan
            </h3>
            <p className="text-lg text-muted-foreground">
              Start free and upgrade as your collection grows
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {membershipTiers?.map((tier, index) => (
              <div
                key={tier?.name}
                className={`relative bg-card border rounded-2xl p-8 premium-hover ${
                  tier?.popular
                    ? 'border-primary shadow-gold scale-105'
                    : 'border-border'
                }`}
              >
                {tier?.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm font-bold">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h4 className="text-2xl font-bold text-card-foreground mb-2">
                    {tier?.name}
                  </h4>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-primary">
                      {tier?.price}
                    </span>
                    {tier?.price !== "Free" && (
                      <span className="text-muted-foreground ml-2">
                        {tier?.period}
                      </span>
                    )}
                  </div>
                  <p className="text-muted-foreground">
                    {tier?.description}
                  </p>
                </div>

                <div className="space-y-4 mb-8">
                  {tier?.features?.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center space-x-3">
                      <div className="w-5 h-5 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="Check" size={12} className="text-success" />
                      </div>
                      <span className="text-muted-foreground text-sm">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                <Button
                  variant={tier?.popular ? "default" : "outline"}
                  size="lg"
                  className={`w-full ${tier?.popular ? 'liquid-gold' : ''} premium-hover`}
                  onClick={() => window.location.href = tier?.href}
                >
                  {tier?.cta}
                </Button>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Final CTA with Guarantee */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center bg-gradient-to-r from-primary/5 to-warning/5 rounded-3xl p-12"
        >
          <div className="max-w-3xl mx-auto">
            <h3 className="text-3xl font-bold text-foreground mb-6">
              Start Your Collection Journey Today
            </h3>
            <p className="text-lg text-muted-foreground mb-8">
              Join the community of serious collectors who trust Crazy Rare Club with their most valuable possessions. No risk, no commitment required.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 mb-8">
              <div className="flex items-center space-x-2 text-success">
                <Icon name="Shield" size={20} />
                <span className="font-medium">30-day money-back guarantee</span>
              </div>
              <div className="flex items-center space-x-2 text-success">
                <Icon name="Clock" size={20} />
                <span className="font-medium">Setup in under 5 minutes</span>
              </div>
              <div className="flex items-center space-x-2 text-success">
                <Icon name="Users" size={20} />
                <span className="font-medium">Join 15,000+ collectors</span>
              </div>
            </div>

            <Button
              variant="default"
              size="xl"
              iconName="ArrowRight"
              iconPosition="right"
              className="px-16 py-4 text-xl font-bold liquid-gold premium-hover"
              onClick={() => window.location.href = '/registration'}
            >
              Get Started Free
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CallToAction;